import React, { useEffect, useState, useMemo } from "react";

// MUI
import { Box, Grid } from "@mui/material";

// Hooks
import {
  useAppDispatch,
  useAppSelector,
} from "../../../redux/hooks/redux-hooks";

// Utils
import {
  createAutocompleteOptions,
  yesOrNoAutoCompleteOptions,
  isValidArr,
  getDecimalValue,
} from "../../../utility/utils";

// Config
import {
  valueSlabs,
  projectValueSlabOptions,
  opportunityQualificationOptions,
} from "../../../utility/config";

// Actions
import {
  setBaseLoad,
  setBUID,
  setSBU,
  setSBG,
  setCSGO,
  setCustomerName,
  setEstimatedValue,
  setIsGreenCustomer,
  setProjectName,
  setSegmentClassification,
  setSegmentLOBID,
  setIfProjectNameExists,
  setValueSlab,
  setIsNewProcessApplicable,
  setOtherFundingSource,
  setContractType,
  setOthersContactType,
  setFundingSource,
  setJVPartenerReq,
  setJVPartnerSuggestedName,
  setTechParterReq,
  setTechPartnerSuggestedName,
  setCity,
  setCountry,
  setGeography,
  setLicenseRequired,
  setLicenseRequiredNotAvailable,
  setLocation,
  setState,
  setAnyOtherAdditionalInfo,
  setBidSubmissionDate,
  setOpportunityQualification,
  setCountryCorruptionRating,
} from "../../../redux/actions/ProjectActions";
import { fetchParticularCustomerTypeSP } from "../../../redux/actions/CustomerTypeSPActions";
import { fetchParticularSegmentClassSP } from "../../../redux/actions/SegmentClassSPActions";
import { fetchBUsBySBU } from "../../../redux/actions/BUActions";
import { fetchSegmentsByBU } from "../../../redux/actions/SegmentLOBActions";
import {
  fetchUserSBUs,
  setParticularUserSBU,
} from "../../../redux/actions/SBUActions";
import {
  getStatesForCountry,
  resetStates,
} from "../../../redux/actions/StateActions";
import { getAllCountries } from "../../../redux/actions/CountryActions";

// Services
import ProjectService from "../../../services/ProjectService";
import CustomerService from "../../../services/CustomerService";

// Components
import ComboBox from "../../ComboBox";
import TextBox from "../../TextBox";
import CustomDatePicker from "../../DatePicker";

// Types
import { AddEdit } from "../../../SectionWiseClassification";
type Props = {
  type: AddEdit;
};

const BasicInfoCreationSection = ({ type }: Props) => {
  const dispatch = useAppDispatch();
  const [projectNameExists, setProjectNameExists] = useState<boolean>(false);

  const get_Project_Object = useAppSelector(
    (state) => state.project.project_Object
  );
  const get_LoggedInUser = useAppSelector((state) => state.user.loggedIn_User);
  const customerTypeSP = useAppSelector(
    (state) => state.customerTypeSP.particular_customerTypeSP
  );
  const get_segmentClassSP = useAppSelector(
    (state) => state.segmentClassSP.particular_segmentClassSP
  );
  const all_sbgs = useAppSelector((state) => state.sbg.all_SBGs);
  const all_sbus = useAppSelector((state) => state.sbu.userSBUs);
  const all_bus = useAppSelector((state) => state.bu.bus_by_SBU);
  const all_segmentLOBs = useAppSelector(
    (state) => state.segmentLOB.segmentsByBu
  );
  const all_customers = useAppSelector((state) => state.customer.all_Customers);
  const all_SegmentClassifications = useAppSelector(
    (state) => state.segmentClassification.all_SegmentClassifications
  );
  const all_CustomerTypes = useAppSelector(
    (state) => state.customerType.all_CustomerTypes
  );
  const all_Funding_Source = useAppSelector(
    (state) => state.finance.all_Finances
  );
  const all_ContractTypes = useAppSelector(
    (state) => state.contractType.all_ContractTypes
  );
  const all_Geographies = useAppSelector(
    (state) => state.geography.all_Geographies
  );
  const allCountries = useAppSelector((state) => state.country.all_Countries);
  const allStates = useAppSelector((state) => state.state.all_States);

  const sbgOptions = useMemo(() => {
    return createAutocompleteOptions(all_sbgs, "sbgid", "name");
  }, [all_sbgs]);

  const segmentLOBOptions = useMemo(() => {
    return createAutocompleteOptions(all_segmentLOBs, "segmentlobid", "name");
  }, [all_segmentLOBs]);

  const sbuOptions = useMemo(() => {
    return createAutocompleteOptions(all_sbus, "sbuid", "sbuName");
  }, [all_sbus]);

  const buOptions = useMemo(() => {
    return createAutocompleteOptions(all_bus, "buid", "name");
  }, [all_bus]);

  const customerOptions = useMemo(() => {
    return createAutocompleteOptions(all_customers, "customerId", "name");
  }, [all_customers]);

  const segmentClassificationOptions = useMemo(() => {
    return createAutocompleteOptions(
      all_SegmentClassifications,
      "name",
      "name"
    );
  }, [all_SegmentClassifications]);

  const customerTypeOptions = useMemo(() => {
    return createAutocompleteOptions(
      all_CustomerTypes,
      "customerTypeId",
      "name"
    );
  }, [all_CustomerTypes]);

  const fundingSourceOptions = useMemo(() => {
    return createAutocompleteOptions(
      all_Funding_Source,
      "fundingSourceId",
      "name"
    );
  }, [all_Funding_Source]);

  const fundingSource = useMemo(() => {
    if (get_Project_Object.fundingSourceId) {
      return all_Funding_Source.find(
        (rec) => rec.fundingSourceId === get_Project_Object.fundingSourceId
      )?.name;
    }
    return "";
  }, [get_Project_Object.fundingSourceId, all_Funding_Source]);

  const contractTypeOptions = useMemo(() => {
    return createAutocompleteOptions(
      all_ContractTypes,
      "contractTypeId",
      "name"
    );
  }, [all_ContractTypes]);

  // To get the contract type from id
  const contractType = useMemo(() => {
    if (get_Project_Object.contractTypeId) {
      return all_ContractTypes.find(
        (rec) => rec.contractTypeId === get_Project_Object.contractTypeId
      )?.name;
    }
    return "";
  }, [get_Project_Object.contractTypeId, all_ContractTypes]);

  const geographyOptions = useMemo(() => {
    return createAutocompleteOptions(all_Geographies, "geographyId", "name");
  }, [all_Geographies]);

  const countryOptions = useMemo(() => {
    return createAutocompleteOptions(
      allCountries,
      "countryCode",
      "countryName"
    );
  }, [allCountries]);

  const stateOptions = useMemo(() => {
    return createAutocompleteOptions(allStates, "stateCode", "stateName");
  }, [allStates]);

  // To get the list of sbus based on login userid
  useEffect(() => {
    if (get_LoggedInUser.userId && get_LoggedInUser.roleId !== 7) {
      dispatch(fetchUserSBUs(get_LoggedInUser.userId));
    }
  }, [get_LoggedInUser, dispatch]);

  // To get the list of sbus for admin
  useEffect(() => {
    if (get_Project_Object.userId && get_LoggedInUser.roleId === 7) {
      dispatch(fetchUserSBUs(get_Project_Object.userId));
    }
  }, [get_Project_Object.userId, get_LoggedInUser.roleId, dispatch]);

  useEffect(() => {
    if (get_Project_Object.customerNameId) {
      dispatch(
        fetchParticularCustomerTypeSP(get_Project_Object.customerNameId)
      );
    }
  }, [get_Project_Object.customerNameId, dispatch]);

  useEffect(() => {
    if (get_Project_Object.segmentLobid && customerTypeSP.customerTypeId) {
      dispatch(
        fetchParticularSegmentClassSP(
          get_Project_Object.segmentLobid,
          customerTypeSP.customerTypeId
        )
      );
    }
  }, [
    get_Project_Object.segmentLobid,
    customerTypeSP.customerTypeId,
    dispatch,
  ]);

  useEffect(() => {
    if (get_segmentClassSP && get_segmentClassSP.name) {
      dispatch(setSegmentClassification(get_segmentClassSP.name));
    }

    if (get_segmentClassSP && get_segmentClassSP.name === "Base load") {
      dispatch(setBaseLoad(true));
    } else {
      dispatch(setBaseLoad(false));
    }
  }, [get_segmentClassSP, dispatch]);

  useEffect(() => {
    async function isGreenCustomerOrNot(segmentId: number, customerId: number) {
      const isGreen = await CustomerService.isGreenCustomer(
        segmentId,
        customerId
      );
      if (isGreen === true) {
        dispatch(setIsGreenCustomer(true));
      } else {
        dispatch(setIsGreenCustomer(false));
      }
    }

    isGreenCustomerOrNot(
      get_Project_Object.segmentLobid,
      get_Project_Object.customerNameId
    );
  }, [
    get_Project_Object.customerNameId,
    get_Project_Object.segmentLobid,
    dispatch,
  ]);

  //fetch segments for bu Id
  useEffect(() => {
    if (get_Project_Object.buid) {
      dispatch(fetchSegmentsByBU(get_Project_Object.buid));
      // dispatch(setSegmentLOBID(0));
    }
  }, [get_Project_Object.buid, dispatch]);

  // Set initial sbu value on page load
  useEffect(() => {
    if (isValidArr(all_sbus)) {
      dispatch(setSBU(all_sbus[0].sbuid));
    }
  }, [all_sbus, dispatch]);

  // set sbg and csgo and fetch bus on sbu change
  useEffect(() => {
    if (get_Project_Object.sbuid && isValidArr(all_sbus)) {
      const selectedSBU = all_sbus.find(
        (rec) => rec.sbuid === get_Project_Object.sbuid
      );
      if (selectedSBU) {
        dispatch(setSBG(selectedSBU.sbgid));
        dispatch(setCSGO(selectedSBU.csgoid));
        dispatch(setParticularUserSBU(selectedSBU));
        dispatch(fetchBUsBySBU(selectedSBU.sbuName));
        // dispatch(setBUID(0)); // To reset buid on sbu change
      }
    }
  }, [get_Project_Object.sbuid, all_sbus, dispatch]);

  // set value slab and new process applicable on estimated value
  useEffect(() => {
    if (Number(get_Project_Object.estimatedValue) < 500) {
      dispatch(setValueSlab(valueSlabs.slab500));
      dispatch(setIsNewProcessApplicable(true));
    } else if (Number(get_Project_Object.estimatedValue) < 1000) {
      dispatch(setValueSlab(valueSlabs.slab1000));
      dispatch(setIsNewProcessApplicable(true));
    } else {
      dispatch(setValueSlab(valueSlabs.slabMoreThan1000));
      dispatch(setIsNewProcessApplicable(true));
    }
  }, [get_Project_Object.estimatedValue, dispatch]);

  // To get all countries
  useEffect(() => {
    dispatch(getAllCountries());
  }, [dispatch]);

  // To fetch the country and state dropdown values based on geography
  useEffect(() => {
    if (get_Project_Object.geographyId === 1) {
      const countryName = allCountries.find(
        (rec) => rec.countryCode === "IN"
      )?.countryName;
      dispatch(setCountry(countryName!));
    }
  }, [get_Project_Object.geographyId, allCountries, dispatch]);

  // To fetch the states based on country
  useEffect(() => {
    if (get_Project_Object.country) {
      const country = allCountries.find(
        (rec) => rec.countryName === get_Project_Object.country
      );
      if (country && country.countryCode) {
        dispatch(getStatesForCountry(country.countryCode));
      }
    } else {
      dispatch(resetStates());
    }
  }, [get_Project_Object.country, allCountries, dispatch]);

  async function CheckIfProjectNameExists(name: string) {
    if (name) {
      const exists: boolean = await ProjectService.checkIfProjectNameExists(
        name
      );
      setProjectNameExists(exists);
      dispatch(setIfProjectNameExists(exists));
    }
  }

  const onChangeTextDataLength = (val: string) => {
    CheckIfProjectNameExists(val);
    dispatch(setProjectName(val));
  };

  const handleSBUOnChange = (value: string | number): void => {
    if (typeof value === "string") {
      value = parseInt(value, 10);
    }
    dispatch(setSBU(value));
  };

  const handleBUOnChange = (value: string | number): void => {
    if (typeof value === "string") {
      value = parseInt(value, 10);
    }
    dispatch(setBUID(value));
  };

  const handleSegmentLOBOnChange = (value: string | number) => {
    if (typeof value === "string") {
      value = parseInt(value, 10);
    }
    dispatch(setSegmentLOBID(value));
  };

  const handleCustomerNameOnChange = (value: string | number) => {
    if (typeof value === "string") {
      value = parseInt(value, 10);
    }
    dispatch(setCustomerName(value));
    dispatch(fetchParticularCustomerTypeSP(value));
  };

  const handleSegClassOnChange = (value: string | number) => {
    if (typeof value === "number") {
      value = value.toString();
    }
    dispatch(setSegmentClassification(value));
  };

  const onChangeEstimatedValueOnChange = (val: string) => {
    let value = getDecimalValue(val);
    dispatch(
      setEstimatedValue(!value ? get_Project_Object.estimatedValue : value)
    );
    // if (!isNaN(Number(value))) {
    //   dispatch(setEstimatedValue(Number(value)));
    // } else {
    //   dispatch(setEstimatedValue(0));
    // }
  };

  const handleFundingSourceOnChange = (value: string | number) => {
    if (typeof value === "string") {
      value = parseInt(value, 10);
    }
    dispatch(setFundingSource(value));
  };

  const onChangeOtherFundingSource = (val: string) => {
    dispatch(setOtherFundingSource(val));
  };

  const handleContractTypeOnChange = (value: string | number) => {
    if (typeof value === "string") {
      value = parseInt(value, 10);
    }
    dispatch(setContractType(value));
  };

  const onChangeOthersContractType = (val: string) => {
    dispatch(setOthersContactType(val));
  };

  const onChangeCountryCorruptionRating = (val: string) => {
    dispatch(setCountryCorruptionRating(val));
  };

  const onChangeLicenseProcurementNextSteps = (val: string) => {
    dispatch(setLicenseRequiredNotAvailable(val));
  };

  const handleGeographyOnChange = (value: string | number) => {
    if (typeof value === "string") {
      value = parseInt(value, 10);
    }
    dispatch(setCountry(""));
    dispatch(setState(""));
    dispatch(setCity(""));
    dispatch(setGeography(value));
  };

  const handleStateOnChange = (value: string | number): void => {
    const name = allStates.find((rec) => rec.stateCode === value)?.stateName;
    dispatch(setState(name!));
    dispatch(setCity(""));
  };

  const handleCityOnChange = (value: string) => {
    dispatch(setCity(value));
  };

  const onChangeLocation = (val: string) => {
    dispatch(setLocation(val));
  };

  const handleLicenseReqOnChange = (value: string | number) => {
    if (typeof value === "number") {
      value = value.toString();
    }
    dispatch(setLicenseRequired(value === "1" ? true : false));
  };

  const handleCountryOnChange = (value: string | number): void => {
    const name = allCountries.find(
      (rec) => rec.countryCode === value
    )?.countryName;
    dispatch(setCountry(name!));
    dispatch(setState(""));
    dispatch(setCity(""));
  };

  const onChangeAdditionalInformation = (val: string) => {
    dispatch(setAnyOtherAdditionalInfo(val));
  };

  const handleJVPartnerReqOnChange = (value: string | number) => {
    if (typeof value === "number") {
      value = value.toString();
    }
    if (value === "0") {
      dispatch(setJVPartnerSuggestedName(""));
    }
    dispatch(setJVPartenerReq(value === "1" ? true : false));
  };

  const handleTechPartnerReqOnChange = (value: string | number) => {
    if (typeof value === "number") {
      value = value.toString();
    }
    if (value === "0") {
      dispatch(setTechPartnerSuggestedName(""));
    }
    dispatch(setTechParterReq(value === "1" ? true : false));
  };

  const onChangeJVPartnerSuggestedName = (val: string) => {
    dispatch(setJVPartnerSuggestedName(val));
  };

  const onChangeTechPartnerSuggestedName = (val: string) => {
    dispatch(setTechPartnerSuggestedName(val));
  };

  const handleBidSubmissionDate = (date: Date | null): void => {
    if (date) {
      dispatch(setBidSubmissionDate(date));
    }
  };

  const handleOpportunityQualification = (value: string | number) => {
    if (typeof value === "number") {
      value = value.toString();
    }
    dispatch(setOpportunityQualification(value));
  };
  const today = new Date();
  const minDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  return (
    <Box
      sx={{
        padding: "1em",
        border: "1px solid rgb(0 0 0 / 18%)",
        borderRadius: "0.375rem",
      }}
    >
      <Grid container rowSpacing={2} columnSpacing={3} alignItems="start">
        <Grid item xs={12}>
          <TextBox
            changeHandler={onChangeTextDataLength}
            label="Project Name"
            placeholder="Enter Project Name"
            info="Character Limit: 200. If additional details, provide them under the field 'Any other relevant information'"
            multiline
            maxRows={2}
            inputProps={{ maxLength: 200 }}
            variant="standard"
            required
            fullWidth
            disabled={type === "edit" ? true : false}
            value={get_Project_Object.projectName}
            projectnameexists={projectNameExists ? "true" : "false"}
            error={projectNameExists ? true : false}
            helperText={projectNameExists ? "Project name already exists" : ""}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <ComboBox
            options={sbgOptions}
            value={get_Project_Object.sbgid}
            label="SBG"
            placeholder="Select an SBG"
            required={true}
            disabled={true}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <ComboBox
            options={sbuOptions}
            value={get_Project_Object.sbuid}
            label="SBU"
            placeholder="Select SBU"
            required={true}
            onChange={handleSBUOnChange}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <ComboBox
            options={buOptions}
            value={get_Project_Object.buid}
            label="BU/LOB"
            placeholder="Select BU/LOB"
            onChange={handleBUOnChange}
            required={true}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <ComboBox
            placeholder="Select Segment"
            label="Segment"
            options={segmentLOBOptions}
            value={get_Project_Object.segmentLobid}
            required={true}
            onChange={handleSegmentLOBOnChange}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <ComboBox
            placeholder="Select Customer Name"
            label="Customer Name"
            info="In case you don't find Customer Name in the list, please approach Admin"
            options={customerOptions}
            value={get_Project_Object.customerNameId}
            required={true}
            onChange={handleCustomerNameOnChange}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <ComboBox
            placeholder="Select Segment Classification"
            label="Segment Classification"
            options={segmentClassificationOptions}
            value={get_segmentClassSP ? get_segmentClassSP.name : undefined}
            required={true}
            disabled={true}
            onChange={handleSegClassOnChange}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <ComboBox
            placeholder="Select Base Load"
            label="Base Load"
            options={yesOrNoAutoCompleteOptions}
            value={
              get_Project_Object.isBaseLoad === true
                ? yesOrNoAutoCompleteOptions[1].value
                : yesOrNoAutoCompleteOptions[0].value
            }
            required={true}
            disabled={true}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <ComboBox
            placeholder="Select Green Customer"
            label="Green Customer"
            options={yesOrNoAutoCompleteOptions}
            value={
              get_Project_Object.isGreenListCustomer === true
                ? yesOrNoAutoCompleteOptions[1].value
                : yesOrNoAutoCompleteOptions[0].value
            }
            required={true}
            disabled={true}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <ComboBox
            placeholder="Select Type of Customer"
            label="Type of Customer"
            options={customerTypeOptions}
            value={customerTypeSP.customerTypeId}
            required={true}
            disabled={true}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <TextBox
            changeHandler={onChangeEstimatedValueOnChange}
            label="Estimated value (INR Cr)"
            size="small"
            type="number"
            fullWidth
            required={get_Project_Object.estimatedValue === 0 ? false : true}
            value={get_Project_Object.estimatedValue || 0}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <ComboBox
            placeholder="Select Value Slab"
            label="Value Slab"
            options={projectValueSlabOptions}
            value={get_Project_Object.valueSlab}
            required={true}
            disabled={true}
          />
        </Grid>
        {/* <Grid item xs={12} sm={4} md={3}>
          <ComboBox
            placeholder="Select New Process Applicable"
            label="New Process Applicable"
            options={yesOrNoAutoCompleteOptions}
            value={
              get_Project_Object.isNewProcessApplicable === true
                ? yesOrNoAutoCompleteOptions[1].value
                : yesOrNoAutoCompleteOptions[0].value
            }
            required={true}
            disabled={true}
          />
        </Grid> */}
        <Grid item xs={12} sm={4} md={3}>
          <ComboBox
            placeholder="Select Finances (Funding source etc)"
            label="Finances (Funding source etc)"
            info="Select Funding Source for the Project"
            options={fundingSourceOptions}
            value={get_Project_Object.fundingSourceId}
            required={true}
            onChange={handleFundingSourceOnChange}
          />
        </Grid>
        {fundingSource === "Others" && (
          <Grid item xs={12} sm={4} md={3}>
            <TextBox
              label="Other Funding Source"
              placeholder="Enter Other Funding Source"
              size="small"
              fullWidth
              changeHandler={onChangeOtherFundingSource}
              value={get_Project_Object?.otherFundingSource || ""}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={4} md={3}>
          <ComboBox
            placeholder="Select Contract Type"
            label="Contract Type"
            options={contractTypeOptions}
            value={get_Project_Object.contractTypeId || 0}
            required={true}
            onChange={handleContractTypeOnChange}
          />
        </Grid>
        {contractType === "Others" && (
          <Grid item xs={12} sm={4} md={3}>
            <TextBox
              changeHandler={onChangeOthersContractType}
              value={get_Project_Object?.otherContractType || ""}
              label="Other Contract Type"
              placeholder="Enter Other Contract Type"
              required
              fullWidth
              size="small"
            />
          </Grid>
        )}
        <Grid item xs={12} sm={4} md={3}>
          <ComboBox
            placeholder="JV Partner Requirement"
            label="Is JV Partner Required?"
            options={yesOrNoAutoCompleteOptions}
            value={
              get_Project_Object.isJvpartnerRequired === true
                ? yesOrNoAutoCompleteOptions[1].value
                : yesOrNoAutoCompleteOptions[0].value
            }
            onChange={handleJVPartnerReqOnChange}
          />
        </Grid>

        <Grid item xs={12} sm={4} md={3}>
          <TextBox
            label="JV Partner Suggested Name"
            placeholder="Please enter text here"
            info="Suggest names of potential partners that can be considered for this bid"
            size="small"
            fullWidth
            disabled={!get_Project_Object.isJvpartnerRequired}
            value={get_Project_Object.jvpartnerSuggestedName || ""}
            changeHandler={onChangeJVPartnerSuggestedName}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <ComboBox
            placeholder="Tech Partner Requirement"
            label="Is Tech Partner Required?"
            options={yesOrNoAutoCompleteOptions}
            value={
              get_Project_Object.isTechPartnerRequired === true
                ? yesOrNoAutoCompleteOptions[1].value
                : yesOrNoAutoCompleteOptions[0].value
            }
            onChange={handleTechPartnerReqOnChange}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <TextBox
            label="Tech Partner Suggested Name"
            placeholder="Please enter text here"
            info="Suggest names of potential partners that can be considered for this bid"
            size="small"
            fullWidth
            disabled={!get_Project_Object.isTechPartnerRequired}
            value={get_Project_Object.techPartnerSuggestedName || ""}
            changeHandler={onChangeTechPartnerSuggestedName}
          />
        </Grid>

        <Grid item xs={12} sm={4} md={3}>
          <ComboBox
            options={geographyOptions}
            value={get_Project_Object.geographyId}
            placeholder="Select Geography"
            label="Geography"
            onChange={handleGeographyOnChange}
            required={true}
          />
        </Grid>
        {get_Project_Object.geographyId === 2 && (
          <>
            <Grid item xs={12} sm={4} md={3}>
              <ComboBox
                placeholder="Please enter text here"
                label="License Requirement (if international)"
                options={yesOrNoAutoCompleteOptions}
                value={
                  get_Project_Object.isLicenseRequired === true
                    ? yesOrNoAutoCompleteOptions[1].value
                    : yesOrNoAutoCompleteOptions[0].value
                }
                required={true}
                onChange={handleLicenseReqOnChange}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <TextBox
                label="If required license not available"
                required
                fullWidth
                size="small"
                value={get_Project_Object.licenseProcurementNextSteps || ""}
                changeHandler={onChangeLicenseProcurementNextSteps}
              />
            </Grid>
          </>
        )}
        {get_Project_Object.geographyId === 2 && (
          <Grid item xs={12} sm={4} md={3}>
            <ComboBox
              placeholder="Select Country"
              label="Country"
              options={countryOptions}
              value={
                allCountries.find(
                  (rec) => rec.countryName === get_Project_Object.country
                )?.countryCode
              }
              onChange={handleCountryOnChange}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={4} md={3}>
          <ComboBox
            placeholder="Select State"
            label="State"
            options={stateOptions}
            value={
              allStates.find(
                (rec) => rec.stateName === get_Project_Object.state
              )?.stateCode
            }
            onChange={handleStateOnChange}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <TextBox
            label="City"
            placeholder="Enter City"
            size="small"
            fullWidth
            required
            value={get_Project_Object.city || ""}
            changeHandler={handleCityOnChange}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <TextBox
            label="Location"
            placeholder="Enter Location"
            size="small"
            fullWidth
            value={get_Project_Object.location || ""}
            changeHandler={onChangeLocation}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <CustomDatePicker
            label="Bid Submission Date"
            placeholderText="Select a date..."
            required
            selected={get_Project_Object.bidSubmissionDate}
            onChange={handleBidSubmissionDate}
            minDate={minDate}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <ComboBox
            options={opportunityQualificationOptions}
            value={get_Project_Object.opportunityQualification}
            placeholder="Select Lead/Opportunity Qualification"
            label="Lead/Opportunity Qualification"
            onChange={handleOpportunityQualification}
            required={true}
          />
        </Grid>
        {get_Project_Object.geographyId === 2 && (
          <Grid item xs={12}>
            <TextBox
              label="Country corruption rating (TCPI)"
              required
              fullWidth
              size="small"
              value={get_Project_Object.countryCorruptionRating || ""}
              changeHandler={onChangeCountryCorruptionRating}
              webLink="https://www.transparency.org/en/cpi/2022"
              type="number"
            />{" "}
          </Grid>
        )}
        <Grid item xs={12}>
          <TextBox
            label="Any other relevant information"
            placeholder="Any other relevant information"
            multiline
            fullWidth
            maxRows={3}
            variant="standard"
            value={get_Project_Object.additionalInformation || ""}
            changeHandler={onChangeAdditionalInformation}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default BasicInfoCreationSection;
